import { defineStore, storeToRefs } from 'pinia'
import type { Ref } from 'vue'

// Export the main interface for component imports
export interface Discount {
  id: number
  percentage: number
  business_id: string
  created_by: string
  created_at?: string
}

interface DiscountForm {
  percentage: number
}

export const useDiscountsStore = defineStore('discounts', () => {
  const log = useLogger('store:discounts')
  const { get, post, put, delete: del } = useAuthenticatedFetch()
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)
  // State
  const discounts: Ref<Discount[]> = ref([])
  const loading: Ref<boolean> = ref(false)

  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $confirm, $dialog } = nuxtApp

  // Core data fetching
  async function fetchDiscounts(force = false): Promise<void> {
    if (!businessId.value) return

    // Skip fetch if data already exists and not forcing refresh
    if (!force && discounts.value.length > 0) {
      log.info('Using cached discounts')
      return
    }

    loading.value = true
    try {
      const res = await get(`/api/business/${businessId.value}/discounts`)
      discounts.value = Array.isArray(res) ? res : []
    }
    catch (error) {
      log.error('Error fetching discounts:', error)
      useErrorToast('Failed to get discounts')
    }
    finally {
      loading.value = false
    }
  }

  // CRUD operations
  async function createDiscount(formData: DiscountForm): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/discounts`, formData)

      if (res && res.success !== false) {
        useSuccessToast('Discount created successfully')
        await fetchDiscounts(true)
        return true
      }
      else {
        useErrorToast('Failed to create discount')
        return false
      }
    }
    catch (error: any) {
      log.error('Error creating discount:', error)
      useErrorToast(t('Failed to create discount: ') + (error?.data?.message || error?.message || 'Network error'))
      return false
    }
  }

  async function updateDiscount(id: number, formData: DiscountForm): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/discounts/${id}`, formData)

      if (res && res.success !== false) {
        useSuccessToast('Discount updated successfully')
        await fetchDiscounts(true)
        return true
      }
      else {
        useErrorToast('Failed to update discount')
        return false
      }
    }
    catch (error: any) {
      log.error('Error updating discount:', error)
      useErrorToast(t('Failed to update discount: ') + (error?.data?.message || error?.message || 'Network error'))
      return false
    }
  }

  async function deleteDiscount(discount: Discount): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    return new Promise((resolve) => {
      $confirm.require({
        group: 'inline',
        message: t('Do you want to delete this discount permanently?'),
        header: t('Delete discount'),
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
          label: t('Cancel'),
          severity: 'secondary',
          outlined: true,
        },
        acceptProps: {
          label: t('Delete'),
          severity: 'danger',
        },
        accept: async () => {
          try {
            const res = await del(`/api/business/${businessId.value}/discounts/${discount.id}`)

            if (res && res.success === false) {
              useErrorToast('Error deleting discount')
              resolve(false)
            }
            else {
              useSuccessToast('Discount deleted successfully')
              await fetchDiscounts(true)
              resolve(true)
            }
          }
          catch (error: any) {
            log.error('Error deleting discount:', error)
            useErrorToast(t('Failed to delete discount: ') + (error?.data?.message || error?.message || 'Network error'))
            resolve(false)
          }
        },
        reject: () => {
          // User cancelled deletion
          resolve(false)
        },
      })
    })
  }

  // Dialog methods for modal operations
  async function openNewDiscountDialog(): Promise<void> {
    const { default: DiscountForm } = await import('~/components/Discounts/DiscountForm.vue')

    $dialog.open(DiscountForm, {
      props: {
        header: t('Add new discount'),
        modal: true,
        position: 'center',
        style: { width: '450px' },
        breakpoints: {
          '960px': '75vw',
          '641px': '90vw',
        },
      },
    })
  }

  async function openEditDiscountDialog(discount: Discount): Promise<void> {
    const { default: DiscountEditForm } = await import('~/components/Discounts/DiscountEditForm.vue')

    $dialog.open(DiscountEditForm, {
      props: {
        header: t('Edit discount'),
        modal: true,
        position: 'center',
        style: { width: '450px' },
        breakpoints: {
          '960px': '75vw',
          '641px': '90vw',
        },
      },
      data: {
        discount,
      },
    })
  }

  // Reset method to clear all state
  function reset(): void {
    discounts.value = []
    loading.value = false
  }

  return {
    // State
    discounts,
    loading,

    // Actions
    fetchDiscounts,
    createDiscount,
    updateDiscount,
    deleteDiscount,
    openNewDiscountDialog,
    openEditDiscountDialog,

    // Reset method
    reset,
  }
}, {
  persist: {
    key: 'discounts',
    storage: localStorage,
    paths: ['discounts'],
  },
})

// Factory function for easy initialization
export const createDiscountsStore = async (businessIdParam: string) => {
  const discountsStore = useDiscountsStore()
  const businessStore = useBusinessStore()

  businessStore.setBusinessId(businessIdParam)
  await discountsStore.fetchDiscounts()

  return discountsStore
}
